// Icon generation and color extraction utilities
const ZIP_PREFIX = 'easyappicon-icons-'

// Function to extract dominant color from image
export const extractDominantColor = (imageUrl) => {
  return new Promise((resolve) => {
    // Check if we're in browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve('#ffffff')
      return
    }
    
    const img = document.createElement('img')
    img.crossOrigin = 'anonymous'
    
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      // Set canvas size to image size (but limit to reasonable size for performance)
      const maxSize = 100
      const ratio = Math.min(maxSize / img.width, maxSize / img.height)
      canvas.width = img.width * ratio
      canvas.height = img.height * ratio
      
      // Draw image to canvas
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      
      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const pixels = imageData.data
      
      // Count color frequency
      const colorCount = {}
      
      // Sample every 4th pixel for performance
      for (let i = 0; i < pixels.length; i += 16) {
        const r = pixels[i]
        const g = pixels[i + 1]
        const b = pixels[i + 2]
        const alpha = pixels[i + 3]
        
        // Skip transparent pixels
        if (alpha < 128) continue
        
        // Round colors to reduce variations
        const roundedR = Math.round(r / 10) * 10
        const roundedG = Math.round(g / 10) * 10
        const roundedB = Math.round(b / 10) * 10
        
        const colorKey = `${roundedR},${roundedG},${roundedB}`
        colorCount[colorKey] = (colorCount[colorKey] || 0) + 1
      }
      
      // Find most frequent color
      let maxCount = 0
      let dominantColor = '128,128,128' // default gray
      
      for (const [color, count] of Object.entries(colorCount)) {
        if (count > maxCount) {
          maxCount = count
          dominantColor = color
        }
      }
      
      // Convert to hex
      const [r, g, b] = dominantColor.split(',').map(Number)
      
      // Ensure values are valid numbers and within RGB range
      const validR = Math.max(0, Math.min(255, Math.floor(r || 0)))
      const validG = Math.max(0, Math.min(255, Math.floor(g || 0)))
      const validB = Math.max(0, Math.min(255, Math.floor(b || 0)))
      
      const hex = `#${validR.toString(16).padStart(2, '0')}${validG.toString(16).padStart(2, '0')}${validB.toString(16).padStart(2, '0')}`
      
      resolve(hex)
    }
    
    img.onerror = () => resolve('#ffffff') // fallback to white
    img.src = imageUrl
  })
}

// Function to generate comprehensive icon package
export const generateIcons = async (uploadedImage, backgroundColor, iconName, padding, showBadge = false, badgeText = '', badgeColor = '#333333', outputFormat = 'png', monochromeImage = null) => {
  try {
    // Dynamic imports for JSZip and FileSaver
    const JSZip = (await import('jszip')).default
    const FileSaver = await import('file-saver')
    
    const zip = new JSZip()
    
    // Android icon sizes and their corresponding density folders
    const androidSizes = [
      { name: 'ldpi', size: 36, density: '0.75x', adaptiveForegroundSize: null }, // No adaptive foreground for ldpi
      { name: 'mdpi', size: 48, density: '1x', adaptiveForegroundSize: 108 },
      { name: 'hdpi', size: 72, density: '1.5x', adaptiveForegroundSize: 162 },
      { name: 'xhdpi', size: 96, density: '2x', adaptiveForegroundSize: 216 },
      { name: 'xxhdpi', size: 144, density: '3x', adaptiveForegroundSize: 324 },
      { name: 'xxxhdpi', size: 192, density: '4x', adaptiveForegroundSize: 432 }
    ]
    
    // iOS icon sizes based on Contents.json structure
    const iosSizes = [
      // iPhone icons
      { name: 'iPhone Notification @2x', size: 40, filename: 'Icon-App-20x20@2x.png' },
      { name: 'iPhone Notification @3x', size: 60, filename: 'Icon-App-20x20@3x.png' },
      { name: 'iPhone Settings @1x', size: 29, filename: 'Icon-App-29x29@1x.png' },
      { name: 'iPhone Settings @2x', size: 58, filename: 'Icon-App-29x29@2x.png' },
      { name: 'iPhone Settings @3x', size: 87, filename: 'Icon-App-29x29@3x.png' },
      { name: 'iPhone Spotlight @2x', size: 80, filename: 'Icon-App-40x40@2x.png' },
      { name: 'iPhone Spotlight @3x', size: 120, filename: 'Icon-App-40x40@3x.png' },
      { name: 'iPhone App @2x', size: 120, filename: 'Icon-App-60x60@2x.png' },
      { name: 'iPhone App @3x', size: 180, filename: 'Icon-App-60x60@3x.png' },
      { name: 'iPhone @2x (76pt)', size: 152, filename: 'Icon-App-76x76@2x.png' },
      
      // iPad icons
      { name: 'iPad Notification @1x', size: 20, filename: 'Icon-App-20x20@1x.png' },
      { name: 'iPad Notification @2x', size: 40, filename: 'Icon-App-20x20@2x.png' },
      { name: 'iPad Settings @1x', size: 29, filename: 'Icon-App-29x29@1x.png' },
      { name: 'iPad Settings @2x', size: 58, filename: 'Icon-App-29x29@2x.png' },
      { name: 'iPad Spotlight @1x', size: 40, filename: 'Icon-App-40x40@1x.png' },
      { name: 'iPad Spotlight @2x', size: 80, filename: 'Icon-App-40x40@2x.png' },
      { name: 'iPad App @1x', size: 76, filename: 'Icon-App-76x76@1x.png' },
      { name: 'iPad App @2x', size: 152, filename: 'Icon-App-76x76@2x.png' },
      { name: 'iPad Pro @2x', size: 167, filename: 'Icon-App-83.5x83.5@2x.png' },
      
      // App Store
      { name: 'App Store', size: 1024, filename: 'ItunesArtwork@2x.png' }
    ]
    
    // Helper function to add badge to any icon
    const addBadgeToCanvas = (ctx, canvasSize, iconSize, iconX, iconY, showBadge, badgeText, badgeColor, isAdaptiveForeground = false) => {
      if (showBadge && badgeText) {
        // Save the current context state
        ctx.save()
        
        let badgeBackgroundX, badgeBackgroundY, badgeBackgroundWidth, badgeBackgroundHeight
        let textCenterX, textCenterY, fontSize
        
        if (isAdaptiveForeground) {
          // For adaptive foreground: keep text size/position same as legacy/round, but extend badge background
          const badgeHeight = iconSize * 0.33 // Same scaling as before for text positioning
          const textBadgeY = iconY + iconSize - badgeHeight
          fontSize = badgeHeight * 0.25 // Same scaling as before for text positioning
          
          // Text positioning (same as legacy/round)
          textCenterX = iconX + (canvasSize / 2) 
          textCenterY = textBadgeY + (badgeHeight / 4)
          
          // Fill entire bottom area with badge color - from badge position to bottom of canvas
          badgeBackgroundX = iconX
          badgeBackgroundY = textBadgeY
          badgeBackgroundWidth = canvasSize
          badgeBackgroundHeight = (iconY + canvasSize) - textBadgeY // Fill to bottom of icon area
          
        } else {
          // For legacy/round/square icons: use the icon area normally
          const badgeHeight = iconSize * 0.25 // 25% of icon height
          const badgeY = iconY + iconSize - badgeHeight
          
          // Badge background matches icon area
          badgeBackgroundX = iconX
          badgeBackgroundY = badgeY
          badgeBackgroundWidth = iconSize
          badgeBackgroundHeight = badgeHeight
          
          // Text positioning
          fontSize = badgeHeight * 0.6
          textCenterX = iconX + (iconSize / 2)
          const verticalNudge = fontSize * 0.08
          textCenterY = badgeY + (badgeHeight / 2) + verticalNudge
        }
        
        // Create a semi-transparent overlay for the badge
        ctx.fillStyle = badgeColor
        ctx.fillRect(badgeBackgroundX, badgeBackgroundY, badgeBackgroundWidth, badgeBackgroundHeight)

        ctx.save()
        
        // Configure text
        ctx.fillStyle = '#ffffff'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        
        // Set font with fallbacks - using the same font stack as the preview
        ctx.font = `${fontSize}px 'Inter', system-ui, sans-serif`
        
        // Add text shadow for better visibility - matching preview style
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)'
        ctx.shadowBlur = 2
        ctx.shadowOffsetY = 1
        
        // Draw text with the same styling as preview, perfectly centered
        ctx.fillText(badgeText, textCenterX, textCenterY)

        // Restore the context state
        ctx.restore()
      }
    }

    // Helper function to calculate image dimensions with padding
    const calculateImageDimensions = (img, containerSize, padding) => {
      let imageScale = 1
      
      // Apply padding/zoom exactly like the preview
      if (padding > 0) {
        // Positive padding: reduce the available area by padding percentage
        const paddingPixels = (padding / 100) * containerSize
        containerSize = containerSize - paddingPixels
      } else if (padding < 0) {
        // Negative padding (zoom): scale up the image
        imageScale = 1 + (Math.abs(padding) / 100)
      }
      
      // Calculate final image dimensions
      const finalImageSize = containerSize * imageScale
      
      // Maintain aspect ratio
      const imgAspectRatio = img.width / img.height
      let drawWidth, drawHeight
      
      if (imgAspectRatio > 1) {
        // Landscape image - fit to width
        drawWidth = finalImageSize
        drawHeight = finalImageSize / imgAspectRatio
      } else {
        // Portrait or square image - fit to height
        drawHeight = finalImageSize
        drawWidth = finalImageSize * imgAspectRatio
      }
      
      return { drawWidth, drawHeight }
    }

    // Generate Android Legacy Icon (rounded corners with shadow)
    const generateAndroidLegacyIcon = (size, uploadedImage, backgroundColor, padding, showBadge, badgeText, badgeColor, format) => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = size
        canvas.height = size
        
        // Set high quality rendering
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        
        // Background color
        ctx.fillStyle = backgroundColor
        
        // Legacy icon with rounded corners, outer padding and shadow (Android style)
        const outerPadding = size * 0.104 // 10.4% outer padding (15/144 from old implementation)
        const iconSize = size - (outerPadding * 2) // Actual icon size after padding
        const borderRadius = iconSize * 0.08 // Border radius (10/114 from old implementation)
        
        // Add shadow effects like the old implementation
        ctx.shadowBlur = size * 0.028 // 4/144 scaled to size
        ctx.shadowOffsetX = size * 0.007 // 1/144 scaled to size
        ctx.shadowOffsetY = size * 0.015 // 2/144 scaled to size
        ctx.shadowColor = "rgba(0, 0, 0, 0.4)" // darkgrey with some transparency
        
        ctx.save()
        ctx.beginPath()
        ctx.roundRect(outerPadding, outerPadding, iconSize, iconSize, borderRadius)
        ctx.fill()
        
        // Clear shadow for clipping
        ctx.shadowColor = "transparent"
        ctx.clip()
        
        // Load and draw the foreground image
        const img = document.createElement('img')
        img.onload = () => {
          // Calculate container size (account for outer padding)
          const containerSize = size - (outerPadding * 2)
          
          // Calculate image dimensions with padding
          const { drawWidth, drawHeight } = calculateImageDimensions(img, containerSize, padding)
          
          // Center the image in the canvas
          const x = (size - drawWidth) / 2
          const y = (size - drawHeight) / 2
          
          // Draw the image with proper positioning
          ctx.drawImage(img, x, y, drawWidth, drawHeight)

          // Add badge if enabled - pass icon area coordinates (accounting for outer padding)
          addBadgeToCanvas(ctx, size, iconSize, outerPadding, outerPadding, showBadge, badgeText, badgeColor)
          
          // Convert to blob with specified format
          const mimeType = format === 'webp' ? 'image/webp' : 'image/png'
          const quality = format === 'webp' ? 0.9 : undefined
          canvas.toBlob((blob) => resolve(blob), mimeType, quality)
        }
        img.src = uploadedImage
      })
    }

    // Generate Android Round Icon (circular with shadow)
    const generateAndroidRoundIcon = (size, uploadedImage, backgroundColor, padding, showBadge, badgeText, badgeColor, format) => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = size
        canvas.height = size
        
        // Set high quality rendering
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        
        // Background color
        ctx.fillStyle = backgroundColor
        
        const outerPadding = size * 0.08 // 8% outer padding (12/144 from old implementation)
        const iconSize = size - outerPadding // Actual icon size after padding

        // Create circular mask for round icon with shadow (following drawCircleFree pattern)
        ctx.shadowBlur = iconSize * 0.028 // 4/144 scaled to size
        ctx.shadowOffsetX = 0 // 0/144 scaled to size  
        ctx.shadowOffsetY = iconSize * 0.028 // 4/144 scaled to size
        ctx.shadowColor = "rgba(0, 0, 0, 0.4)" // darkgrey with some transparency

        ctx.save()
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, iconSize / 2, 0, 2 * Math.PI)
        ctx.fill()
        ctx.clip()
        
        // Load and draw the foreground image
        const img = document.createElement('img')
        img.onload = () => {
          // Calculate image dimensions with padding - use iconSize as container size to account for outer padding
          const { drawWidth, drawHeight } = calculateImageDimensions(img, iconSize, padding)
          
          // Center the image in the canvas
          const x = (size - drawWidth) / 2
          const y = (size - drawHeight) / 2
          
          // Clear any shadows before drawing the image
          ctx.shadowColor = "transparent"
          
          // Draw the image with proper positioning
          ctx.drawImage(img, x, y, drawWidth, drawHeight)

          // Add badge if enabled - pass icon area coordinates (centered circle)
          const iconX = size / 2 - iconSize / 2
          const iconY = size / 2 - iconSize / 2
          addBadgeToCanvas(ctx, size, iconSize, iconX, iconY, showBadge, badgeText, badgeColor)
          
          // Convert to blob with specified format
          const mimeType = format === 'webp' ? 'image/webp' : 'image/png'
          const quality = format === 'webp' ? 0.9 : undefined
          canvas.toBlob((blob) => resolve(blob), mimeType, quality)
        }
        img.src = uploadedImage
      })
    }

    // Generate Square Icon (iOS style, no border radius, no shadow)
    const generateSquareIcon = (size, uploadedImage, backgroundColor, padding, showBadge, badgeText, badgeColor, format) => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = size
        canvas.height = size
        
        // Set high quality rendering
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        
        // Background color
        ctx.fillStyle = backgroundColor
        
        // Square icon with no border radius (for iOS) - no shadow
        ctx.fillRect(0, 0, size, size)
        
        // Load and draw the foreground image
        const img = document.createElement('img')
        img.onload = () => {
          // Calculate image dimensions with padding
          const { drawWidth, drawHeight } = calculateImageDimensions(img, size, padding)
          
          // Center the image in the canvas
          const x = (size - drawWidth) / 2
          const y = (size - drawHeight) / 2
          
          // Draw the image with proper positioning
          ctx.drawImage(img, x, y, drawWidth, drawHeight)

          // Add badge if enabled - pass icon area coordinates (full canvas)
          addBadgeToCanvas(ctx, size, size, 0, 0, showBadge, badgeText, badgeColor)
          
          // Convert to blob with specified format
          const mimeType = format === 'webp' ? 'image/webp' : 'image/png'
          const quality = format === 'webp' ? 0.9 : undefined
          canvas.toBlob((blob) => resolve(blob), mimeType, quality)
        }
        img.src = uploadedImage
      })
    }

    // Generate Android Adaptive Foreground Icon (high-DPI versions)
    const generateAndroidAdaptiveForegroundIcon = (size, uploadedImage, padding, showBadge, badgeText, badgeColor, format) => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = size
        canvas.height = size
        
        // Set high quality rendering
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        
        // For adaptive foreground layer, create transparent background - no shape drawn
        ctx.clearRect(0, 0, size, size)
        
        // Load and draw the foreground image
        const img = document.createElement('img')
        img.onload = () => {
          // Apply padding like old implementation: (baseSize - baseSize * padding / 100)
          const paddingReduction = padding > 0 ? (padding / 100) * (size * 2 / 3) : 0
          const imageScale = padding < 0 ? 1 + (Math.abs(padding) / 100) : 1
          
          let finalImageSize = ((size * 2 / 3) - paddingReduction) * imageScale
          
          // Maintain aspect ratio
          const imgAspectRatio = img.width / img.height
          let drawWidth, drawHeight
          
          if (imgAspectRatio > 1) {
            // Landscape image - fit to width
            drawWidth = finalImageSize
            drawHeight = finalImageSize / imgAspectRatio
          } else {
            // Portrait or square image - fit to height  
            drawHeight = finalImageSize
            drawWidth = finalImageSize * imgAspectRatio
          }
          
          // Center the image in the canvas (like loadImageWithOut: (144-width)/2, (144-height)/2)
          const x = (size - drawWidth) / 2
          const y = (size - drawHeight) / 2
          
          // Draw the image with proper positioning
          ctx.drawImage(img, x, y, drawWidth, drawHeight)

          // Add badge if enabled - pass icon area coordinates (full canvas, adaptive foreground)
          addBadgeToCanvas(ctx, size, size, 0, 0, showBadge, badgeText, badgeColor, true)
          
          // Convert to blob with specified format
          const mimeType = format === 'webp' ? 'image/webp' : 'image/png'
          const quality = format === 'webp' ? 0.9 : undefined
          canvas.toBlob((blob) => resolve(blob), mimeType, quality)
        }
        img.src = uploadedImage
      })
    }

    // Helper function to add badge to monochrome canvas with special styling
    const addBadgeToMonochromeCanvas = (ctx, canvasSize, iconSize, iconX, iconY, showBadge, badgeText) => {
      if (showBadge && badgeText) {
        // Save the current context state
        ctx.save()
        
        // For adaptive foreground: keep text size/position same as legacy/round, but extend badge background
        const badgeHeight = canvasSize * 0.33 // Same scaling as before for text positioning
        const textBadgeY = canvasSize - badgeHeight
        const fontSize = badgeHeight * 0.25 // Same scaling as before for text positioning
        
        // Text positioning (same as legacy/round)
        const textCenterX = iconX + (canvasSize / 2) 
        const textCenterY = textBadgeY + (badgeHeight / 4)
        
        // Fill entire bottom area with white color for monochrome - from badge position to bottom of canvas
        const badgeBackgroundX = iconX
        const badgeBackgroundY = textBadgeY
        const badgeBackgroundWidth = canvasSize
        const badgeBackgroundHeight = canvasSize - textBadgeY // Fill to bottom of icon area
        
        // Create white background for monochrome badge
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(badgeBackgroundX, badgeBackgroundY, badgeBackgroundWidth, badgeBackgroundHeight)

        // Configure text to be transparent (use destination-out composite mode)
        ctx.save()
        ctx.globalCompositeOperation = 'destination-out'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        
        // Set font with fallbacks - using the same font stack as the preview
        ctx.font = `${fontSize}px 'Inter', system-ui, sans-serif`
        
        // Draw transparent text by cutting it out from the white background
        ctx.fillText(badgeText, textCenterX, textCenterY)

        // Restore the context state
        ctx.restore()
        ctx.restore()
      }
    }

    // Generate Android Adaptive Monochrome Icon (for themed icons)
    const generateAndroidAdaptiveMonochromeIcon = (size, monochromeImage, padding, showBadge, badgeText, format) => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = size
        canvas.height = size
        
        // Set high quality rendering
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        
        // For adaptive monochrome layer, create transparent background
        ctx.clearRect(0, 0, size, size)
        
        // Load and draw the monochrome image
        const img = document.createElement('img')
        img.onload = () => {
          // Apply padding like foreground implementation
          const paddingReduction = padding > 0 ? (padding / 100) * (size * 2 / 3) : 0
          const imageScale = padding < 0 ? 1 + (Math.abs(padding) / 100) : 1
          
          let finalImageSize = ((size * 2 / 3) - paddingReduction) * imageScale
          
          // Maintain aspect ratio
          const imgAspectRatio = img.width / img.height
          let drawWidth, drawHeight
          
          if (imgAspectRatio > 1) {
            // Landscape image - fit to width
            drawWidth = finalImageSize
            drawHeight = finalImageSize / imgAspectRatio
          } else {
            // Portrait or square image - fit to height  
            drawHeight = finalImageSize
            drawWidth = finalImageSize * imgAspectRatio
          }
          
          // Center the image in the canvas
          const x = (size - drawWidth) / 2
          const y = (size - drawHeight) / 2
          
          // Draw the monochrome image with proper positioning
          ctx.drawImage(img, x, y, drawWidth, drawHeight)

          // Add badge if enabled - pass icon area coordinates (full canvas, adaptive monochrome)
          addBadgeToMonochromeCanvas(ctx, size, finalImageSize, 0, y, showBadge, badgeText)
          
          // Convert to blob with specified format
          const mimeType = format === 'webp' ? 'image/webp' : 'image/png'
          const quality = format === 'webp' ? 0.9 : undefined
          canvas.toBlob((blob) => resolve(blob), mimeType, quality)
        }
        img.src = monochromeImage
      })
    }
    
    // Create Android folder structure
    const androidFolder = zip.folder('android')
    
    // Generate Android icons for each density
    for (const sizeInfo of androidSizes) {
      const densityFolder = androidFolder.folder(`mipmap-${sizeInfo.name}`)
      
      // Generate round and legacy icons at base size
      const roundBlob = await generateAndroidRoundIcon(sizeInfo.size, uploadedImage, backgroundColor, padding, showBadge, badgeText, badgeColor, outputFormat)
      const legacyBlob = await generateAndroidLegacyIcon(sizeInfo.size, uploadedImage, backgroundColor, padding, showBadge, badgeText, badgeColor, outputFormat)
      
      // Use appropriate file extension based on format
      const fileExtension = outputFormat === 'webp' ? 'webp' : 'png'
      
      // Add round and legacy icons to the density folder
      densityFolder.file(`${iconName}_round.${fileExtension}`, roundBlob)
      densityFolder.file(`${iconName}.${fileExtension}`, legacyBlob)
      
      // Generate adaptive foreground icon at larger size (if specified)
      if (sizeInfo.adaptiveForegroundSize) {
        const foregroundBlob = await generateAndroidAdaptiveForegroundIcon(sizeInfo.adaptiveForegroundSize, uploadedImage, padding, showBadge, badgeText, badgeColor, outputFormat)
        densityFolder.file(`${iconName}_foreground.${fileExtension}`, foregroundBlob)
        
        // Generate adaptive monochrome icon if monochrome image is provided
        if (monochromeImage) {
          const monochromeBlob = await generateAndroidAdaptiveMonochromeIcon(sizeInfo.adaptiveForegroundSize, monochromeImage, padding, showBadge, badgeText, outputFormat)
          densityFolder.file(`${iconName}_monochrome.${fileExtension}`, monochromeBlob)
        }
      }
    }

    // Generate additional Android files
    
    // 1. Generate ic_launcher-web.png (512x512 with border radius) - Always PNG
    const webIconBlob = await generateAndroidLegacyIcon(512, uploadedImage, backgroundColor, padding, showBadge, badgeText, badgeColor, 'png')
    androidFolder.file('ic_launcher-web.png', webIconBlob)
    
    // 2. Generate playstore-icon.png (512x512 without border radius) - Always PNG
    const playstoreIconBlob = await generateSquareIcon(512, uploadedImage, backgroundColor, padding, showBadge, badgeText, badgeColor, 'png')
    androidFolder.file('playstore-icon.png', playstoreIconBlob)
    
    // 3. Create mipmap-anydpi-v26 folder and add adaptive icon XMLs
    const anydpiFolder = androidFolder.folder('mipmap-anydpi-v26')
    const adaptiveIconXml = generateAdaptiveIconXml(monochromeImage)
    anydpiFolder.file('ic_launcher.xml', adaptiveIconXml)
    anydpiFolder.file('ic_launcher_round.xml', adaptiveIconXml)
    
    // 4. Create values folder and add background color XML
    const valuesFolder = androidFolder.folder('values')
    const backgroundColorXml = generateBackgroundColorXml(backgroundColor)
    valuesFolder.file('ic_launcher_background.xml', backgroundColorXml)
    
    // Create iOS folder structure
    const iosFolder = zip.folder('ios')
    const appIconSet = iosFolder.folder('AppIcon.appiconset')
    
    // Generate iOS icons for AppIcon.appiconset (iOS always uses PNG)
    for (const sizeInfo of iosSizes) {
      const iconBlob = await generateSquareIcon(sizeInfo.size, uploadedImage, backgroundColor, padding, showBadge, badgeText, badgeColor, 'png')
      appIconSet.file(sizeInfo.filename, iconBlob)
    }

    // Generate and add Contents.json to the iOS AppIcon.appiconset
    const contentsJson = generateContentsJson()
    appIconSet.file('Contents.json', JSON.stringify(contentsJson, null, 4))

    // Generate additional iTunes Artwork files in ios/ folder
    const iTunesArtworkSizes = [
      { size: 512, filename: 'iTunesArtwork@1x.png' },
      { size: 1024, filename: 'iTunesArtwork@2x.png' },
      { size: 1536, filename: 'iTunesArtwork@3x.png' }
    ]

    for (const artworkInfo of iTunesArtworkSizes) {
      const artworkBlob = await generateSquareIcon(artworkInfo.size, uploadedImage, backgroundColor, padding, showBadge, badgeText, badgeColor, 'png')
      iosFolder.file(artworkInfo.filename, artworkBlob)
    }
    
    // Generate and download the ZIP file
    const content = await zip.generateAsync({ type: 'blob' })
    FileSaver.saveAs(content, `${ZIP_PREFIX}${new Date().getTime()}.zip`)
    
    return true // Success
  } catch (error) {
    console.error('Error generating icons:', error)
    throw error
  }
}

// Function to generate Android adaptive icon XML
const generateAdaptiveIconXml = (hasMonochrome = false) => {
  let xml = `<adaptive-icon xmlns:android="http://schemas.android.com/apk/res/android">
  <background android:drawable="@color/ic_launcher_background"/>
  <foreground android:drawable="@mipmap/ic_launcher_foreground"/>`
  
  if (hasMonochrome) {
    xml += `
  <monochrome android:drawable="@mipmap/ic_launcher_monochrome"/>`
  }
  
  xml += `
</adaptive-icon>`
  
  return xml
}

// Function to generate Android background color XML
const generateBackgroundColorXml = (backgroundColor) => {
  return `<resources>
  <color name="ic_launcher_background">${backgroundColor}</color>
</resources>`
}

// Function to generate iOS Contents.json
const generateContentsJson = () => {
  return {
    images: [
      {
        idiom: "iphone",
        size: "20x20",
        scale: "2x",
        filename: "Icon-App-20x20@2x.png"
      },
      {
        idiom: "iphone",
        size: "20x20",
        scale: "3x",
        filename: "Icon-App-20x20@3x.png"
      },
      {
        idiom: "iphone",
        size: "29x29",
        scale: "1x",
        filename: "Icon-App-29x29@1x.png"
      },
      {
        idiom: "iphone",
        size: "29x29",
        scale: "2x",
        filename: "Icon-App-29x29@2x.png"
      },
      {
        idiom: "iphone",
        size: "29x29",
        scale: "3x",
        filename: "Icon-App-29x29@3x.png"
      },
      {
        idiom: "iphone",
        size: "40x40",
        scale: "2x",
        filename: "Icon-App-40x40@2x.png"
      },
      {
        idiom: "iphone",
        size: "40x40",
        scale: "3x",
        filename: "Icon-App-40x40@3x.png"
      },
      {
        idiom: "iphone",
        size: "60x60",
        scale: "2x",
        filename: "Icon-App-60x60@2x.png"
      },
      {
        idiom: "iphone",
        size: "60x60",
        scale: "3x",
        filename: "Icon-App-60x60@3x.png"
      },
      {
        idiom: "iphone",
        size: "76x76",
        scale: "2x",
        filename: "Icon-App-76x76@2x.png"
      },
      {
        idiom: "ipad",
        size: "20x20",
        scale: "1x",
        filename: "Icon-App-20x20@1x.png"
      },
      {
        idiom: "ipad",
        size: "20x20",
        scale: "2x",
        filename: "Icon-App-20x20@2x.png"
      },
      {
        idiom: "ipad",
        size: "29x29",
        scale: "1x",
        filename: "Icon-App-29x29@1x.png"
      },
      {
        idiom: "ipad",
        size: "29x29",
        scale: "2x",
        filename: "Icon-App-29x29@2x.png"
      },
      {
        idiom: "ipad",
        size: "40x40",
        scale: "1x",
        filename: "Icon-App-40x40@1x.png"
      },
      {
        idiom: "ipad",
        size: "40x40",
        scale: "2x",
        filename: "Icon-App-40x40@2x.png"
      },
      {
        idiom: "ipad",
        size: "76x76",
        scale: "1x",
        filename: "Icon-App-76x76@1x.png"
      },
      {
        idiom: "ipad",
        size: "76x76",
        scale: "2x",
        filename: "Icon-App-76x76@2x.png"
      },
      {
        idiom: "ipad",
        size: "83.5x83.5",
        scale: "2x",
        filename: "Icon-App-83.5x83.5@2x.png"
      },
      {
        size: "1024x1024",
        idiom: "ios-marketing",
        scale: "1x",
        filename: "ItunesArtwork@2x.png"
      }
    ],
    info: {
      version: 1,
      author: "easyappicon"
    }
  }
}

// Function to determine if a color is dark or light
export const isColorDark = (hexColor) => {
  const hex = hexColor.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)
  // Calculate luminance using the standard formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance < 0.7
} 